import { SearchableSelect } from "@/lib/shadcn/components/searchable-select";
import { useCallback, useMemo } from "react";
import BarChartStatsCard from "@/lib/shadcn/components/bar-chart-stats-card";
import OccupancyChartStatsCard from "@/lib/shadcn/components/occupancy-chart-stats-card";
import { PeriodName } from "@/models";
import { overViewAdrFormatter, overViewBarChartAdrFormatter, overViewBarChartIncomeFormatter, overViewIncomeFormatter } from "@/utils/formatters";
import { isPreviousPeriodSameAsPreviousYear, periodNameToOverviewCardString } from "@/utils/periods";
import { useAppData } from "@/hooks/useAppData";
import { useUserSettingsDispatch } from "@/store/slices/userSettingsDispatch";
import { GroupStats, PayoutStat } from "@/models/responses";
import { usePeriodDates } from "@/hooks/usePeriodDates";
import { createEmptyGroupStat, useAggregatedStats } from "@/hooks/useAggregatedStats";
import { useGetPayoutsQuery } from "@/store/booking-management-api";
import { useApartmentFilters } from "@/hooks/useApartmentFilters";
import ApartmentFilterBar from "@/lib/components/apartment-filter-bar";

/**
 * Guarded per-unit division. A zero denominator is ordinary here — an empty payout
 * column, or a filter combination that matches no apartment — and `overViewIncomeFormatter`
 * renders the unguarded 0/0 as the literal "NaN €". `> 0` rather than `!== 0` so a
 * negative count (should one ever appear) can't produce a quietly-negative average.
 * Note a trailing `|| 0` is NOT a guard: `Infinity` is truthy and survives it.
 *
 * Every call site must pass the count of the SAME window the numerator came from.
 * Reusing the current window's count for a previous-window numerator rescales last
 * period by the change in portfolio size, and the guard then blanks it to 0 when a
 * filter matches nothing today.
 */
const perUnit = (total: number, units: number): number => (units > 0 ? total / units : 0);

const meanPayout = (stats: PayoutStat[]): number => perUnit(stats.reduce((acc, s) => acc + s.payout, 0), stats.length);

const OverviewPageNew = () => {
    const {
        periodOptions = [],
        yearOptions = [],
        selectedPeriod,
        selectedYear,
        groupIdMap,
        apartmentDetailsMap,
    } = useAppData();

    const { setSelectedPeriod, setSelectedYear } = useUserSettingsDispatch();

    const { predicates: filters } = useApartmentFilters();

    const { current, previousPeriod, previousYear } = usePeriodDates(selectedYear, selectedPeriod);
    const { startDate, endDate } = current;
    const { startDate: previousStartDate, endDate: previousEndDate } = previousPeriod;
    const { startDate: previousYearStartDate, endDate: previousYearEndDate } = previousYear;

    const handleYearChange = (year: string) => {
        setSelectedYear(Number(year));
    };

    const handlePeriodChange = (period: string) => {
        setSelectedPeriod(period as PeriodName);
    };

    const prepareChartData = useCallback(
        (groupStats: GroupStats[], attribute: keyof GroupStats, average: boolean = false) => {
            return groupStats
                .map((groupStat: GroupStats) => {
                    const divider = average ? groupStat["numberOfApartments"] : 1;
                    return {
                        name: groupIdMap.get(groupStat.groupId)?.name || "",
                        value: perUnit(groupStat[attribute] || 0, divider),
                    };
                })
                .sort((a, b) => a.name.localeCompare(b.name));
        },
        [groupIdMap],
    );

    const {
        groupStats: currentGroupStats,
        totalStats,
        isLoading: isCurrentGroupStatsLoading,
        error: currentGroupStatsError,
    } = useAggregatedStats(startDate, endDate, filters);
    const {
        groupStats: previousPeriodGroupStats,
        totalStats: previousPeriodTotalStats,
        isLoading: isPreviousPeriodGroupStatsLoading,
        error: previousPeriodGroupStatsError,
    } = useAggregatedStats(previousStartDate, previousEndDate, filters);
    const {
        groupStats: previousYearGroupStats,
        totalStats: previousYearTotalStats,
        isLoading: isPreviousYearGroupStatsLoading,
        error: previousYearGroupStatsError,
    } = useAggregatedStats(previousYearStartDate, previousYearEndDate, filters);

    const {
        data: currentPayoutStats,
        isLoading: isCurrentPayoutStatsLoading,
        error: currentPayoutStatsError,
    } = useGetPayoutsQuery({
        startDate: startDate,
        endDate: endDate,
    });

    for (const groupStat of currentGroupStats) {
        if (!previousYearGroupStats.map((g) => g.groupId).includes(groupStat.groupId)) {
            previousYearGroupStats.push(createEmptyGroupStat(groupStat.groupId));
        }
        if (!previousPeriodGroupStats.map((g) => g.groupId).includes(groupStat.groupId)) {
            previousPeriodGroupStats.push(createEmptyGroupStat(groupStat.groupId));
        }
    }

    const {
        data: previousPeriodPayoutStats,
        isLoading: isPreviousPeriodPayoutStatsLoading,
        error: previousPeriodPayoutStatsError,
    } = useGetPayoutsQuery({
        startDate: previousStartDate,
        endDate: previousEndDate,
    });

    const {
        data: previousYearPayoutStats,
        isLoading: isPreviousYearPayoutStatsLoading,
        error: previousYearPayoutStatsError,
    } = useGetPayoutsQuery({
        startDate: previousYearStartDate,
        endDate: previousYearEndDate,
    });

    const prepareGroupAveragePayoutData = useCallback(
        (payouts: PayoutStat[]) => {
            const payoutsByGroup = new Map<number, number>();
            const apartmentsByGroup = new Map<number, number>();
            payouts.forEach((payout) => {
                const apartment = apartmentDetailsMap.get(payout.apartmentId);
                if (!apartment) {
                    return;
                }
                for (const filter of filters) {
                    if (!filter(apartment)) {
                        return;
                    }
                }
                const groupId = apartment.groupId;
                if (groupId) {
                    if (!payoutsByGroup.has(groupId)) {
                        payoutsByGroup.set(groupId, 0);
                    }
                    payoutsByGroup.set(groupId, payoutsByGroup.get(groupId)! + payout.payout);
                    apartmentsByGroup.set(groupId, (apartmentsByGroup.get(groupId) || 0) + 1);
                }
            });
            return Array.from(payoutsByGroup.entries())
                .map(([groupId, payout]) => ({
                    name: groupIdMap.get(groupId)?.name || "",
                    // `?? 0` is not a fallback: every key iterated here was counted in the
                    // same block, so the count is always >= 1. Routed through `perUnit`
                    // rather than a `|| 1` that reads like a live guard but can't fire.
                    value: perUnit(payout, apartmentsByGroup.get(groupId) ?? 0),
                }))
                .sort((a, b) => a.name.localeCompare(b.name));
        },
        [apartmentDetailsMap, groupIdMap, filters],
    );

    const { averagePayout, averagePayoutPrevious, averagePayoutPreviousYear, averageGroupPayout, averageGroupPayoutPrevious, averageGroupPayoutPreviousYear } =
        useMemo(() => {
            if (currentPayoutStats === undefined || previousPeriodPayoutStats === undefined || previousYearPayoutStats === undefined) {
                return {
                    averagePayout: 0,
                    averagePayoutPrevious: 0,
                    averagePayoutPreviousYear: 0,
                    averageGroupPayout: [],
                    averageGroupPayoutPrevious: [],
                    averageGroupPayoutPreviousYear: [],
                };
            }
            let current = currentPayoutStats.filter((s) => {
                const apartment = apartmentDetailsMap.get(s.apartmentId);
                if (!apartment) {
                    return false;
                }
                return filters.every((filter) => filter(apartment));
            });
            let previousPeriod = previousPeriodPayoutStats.filter((s) => {
                const apartment = apartmentDetailsMap.get(s.apartmentId);
                if (!apartment) {
                    return false;
                }
                return filters.every((filter) => filter(apartment));
            });
            let previousYear = previousYearPayoutStats.filter((s) => {
                const apartment = apartmentDetailsMap.get(s.apartmentId);
                if (!apartment) {
                    return false;
                }
                return filters.every((filter) => filter(apartment));
            });
            return {
                averagePayout: meanPayout(current),
                averagePayoutPrevious: meanPayout(previousPeriod),
                averagePayoutPreviousYear: meanPayout(previousYear),
                averageGroupPayout: prepareGroupAveragePayoutData(current),
                averageGroupPayoutPrevious: prepareGroupAveragePayoutData(previousPeriod),
                averageGroupPayoutPreviousYear: prepareGroupAveragePayoutData(previousYear),
            };
        }, [currentPayoutStats, previousPeriodPayoutStats, previousYearPayoutStats, prepareGroupAveragePayoutData, apartmentDetailsMap, filters]);

    const isLoading =
        isCurrentGroupStatsLoading ||
        isPreviousPeriodGroupStatsLoading ||
        isPreviousYearGroupStatsLoading ||
        isCurrentPayoutStatsLoading ||
        isPreviousPeriodPayoutStatsLoading ||
        isPreviousYearPayoutStatsLoading ||
        totalStats === undefined ||
        currentGroupStats === undefined ||
        previousPeriodGroupStats === undefined ||
        previousYearGroupStats === undefined ||
        previousPeriodTotalStats === undefined ||
        previousYearTotalStats === undefined ||
        currentPayoutStats === undefined ||
        previousPeriodPayoutStats === undefined ||
        previousYearPayoutStats === undefined;

    const error =
        currentGroupStatsError ||
        previousPeriodGroupStatsError ||
        previousYearGroupStatsError ||
        currentPayoutStatsError ||
        previousPeriodPayoutStatsError ||
        previousYearPayoutStatsError;

    if (isLoading) {
        return <div>Loading...</div>;
    }

    if (error) {
        return <div>Error: {error.toString()}</div>;
    }

    return (
        <div className="flex flex-col space-y-4 p-2 py-0" data-testid="overview-page">
            {/* Header with Selects */}
            <div className="flex flex-col lg:flex-row lg:flex-wrap justify-between gap-2 space-y-2 lg:space-y-0 lg:space-x-2">
                <ApartmentFilterBar />

                <div className="flex flex-col lg:flex-row justify-end space-y-2 lg:space-x-2">
                    <SearchableSelect
                        variant="volt"
                        onChange={(value) => handlePeriodChange(value)}
                        placeholder="Select Period"
                        options={periodOptions || []}
                        selectedValue={selectedPeriod}
                        width="w-auto"
                    />
                    <SearchableSelect
                        variant="volt"
                        onChange={(value) => handleYearChange(value)}
                        placeholder="Select Year"
                        options={yearOptions || []}
                        selectedValue={selectedYear.toString()}
                        width="w-auto"
                    />
                </div>
            </div>

            {/* First Row of Cards */}
            <div id="section-to-print">
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
                    <div data-testid="overview-occupancy-total">
                        <OccupancyChartStatsCard
                            metric="Occupancy"
                            totalOccupancy={totalStats.occupancy}
                            groupOccupancies={prepareChartData(currentGroupStats, "occupancy")}
                            groupOccupanciesPreviousPeriod={prepareChartData(previousPeriodGroupStats, "occupancy")}
                            groupOccupanciesPreviousYear={prepareChartData(previousYearGroupStats, "occupancy")}
                            period={periodNameToOverviewCardString(selectedYear, PeriodName[selectedPeriod as keyof typeof PeriodName])}
                            showPreviousYear={!isPreviousPeriodSameAsPreviousYear(selectedPeriod)}
                            totalPreviousPeriod={previousPeriodTotalStats.occupancy}
                            totalPreviousYear={previousYearTotalStats.occupancy}
                        />
                    </div>

                    <div data-testid="overview-gross-income-total">
                        <BarChartStatsCard
                            metric="Gross Income"
                            total={totalStats.grossIncome}
                            totalValueFormatter={overViewIncomeFormatter}
                            barValueFormatter={overViewBarChartIncomeFormatter}
                            data={prepareChartData(currentGroupStats, "grossIncome")}
                            dataPreviousPeriod={prepareChartData(previousPeriodGroupStats, "grossIncome")}
                            dataPreviousYear={prepareChartData(previousYearGroupStats, "grossIncome")}
                            period={periodNameToOverviewCardString(selectedYear, PeriodName[selectedPeriod as keyof typeof PeriodName])}
                            showPreviousYear={!isPreviousPeriodSameAsPreviousYear(selectedPeriod)}
                            totalPreviousPeriod={previousPeriodTotalStats.grossIncome}
                            totalPreviousYear={previousYearTotalStats.grossIncome}
                        />
                    </div>

                    <BarChartStatsCard
                        metric="Net Income"
                        total={totalStats.netIncome}
                        totalValueFormatter={overViewIncomeFormatter}
                        data={prepareChartData(currentGroupStats, "netIncome")}
                        dataPreviousPeriod={prepareChartData(previousPeriodGroupStats, "netIncome")}
                        dataPreviousYear={prepareChartData(previousYearGroupStats, "netIncome")}
                        barValueFormatter={overViewBarChartIncomeFormatter}
                        period={periodNameToOverviewCardString(selectedYear, PeriodName[selectedPeriod as keyof typeof PeriodName])}
                        showPreviousYear={!isPreviousPeriodSameAsPreviousYear(selectedPeriod)}
                        totalPreviousPeriod={previousPeriodTotalStats.netIncome}
                        totalPreviousYear={previousYearTotalStats.netIncome}
                    />

                    <BarChartStatsCard
                        metric="Gross Adr"
                        total={totalStats.grossAdr}
                        totalValueFormatter={overViewAdrFormatter}
                        data={prepareChartData(currentGroupStats, "grossAdr")}
                        dataPreviousPeriod={prepareChartData(previousPeriodGroupStats, "grossAdr")}
                        dataPreviousYear={prepareChartData(previousYearGroupStats, "grossAdr")}
                        barValueFormatter={overViewBarChartAdrFormatter}
                        period={periodNameToOverviewCardString(selectedYear, PeriodName[selectedPeriod as keyof typeof PeriodName])}
                        showPreviousYear={!isPreviousPeriodSameAsPreviousYear(selectedPeriod)}
                        totalPreviousPeriod={previousPeriodTotalStats.grossAdr}
                        totalPreviousYear={previousYearTotalStats.grossAdr}
                    />
                </div>

                {/* Second Row of Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
                    <BarChartStatsCard
                        metric={"Days Booked"}
                        total={totalStats.daysBooked}
                        totalValueFormatter={(x) => x.toString()}
                        data={prepareChartData(currentGroupStats, "daysBooked")}
                        dataPreviousPeriod={prepareChartData(previousPeriodGroupStats, "daysBooked")}
                        dataPreviousYear={prepareChartData(previousYearGroupStats, "daysBooked")}
                        barValueFormatter={(x) => x.toString()}
                        period={periodNameToOverviewCardString(selectedYear, PeriodName[selectedPeriod as keyof typeof PeriodName])}
                        showPreviousYear={!isPreviousPeriodSameAsPreviousYear(selectedPeriod)}
                        totalPreviousPeriod={previousPeriodTotalStats.daysBooked}
                        totalPreviousYear={previousYearTotalStats.daysBooked}
                    />
                    <BarChartStatsCard
                        metric={"Bookings Received"}
                        total={totalStats.numberOfReservationsReceived}
                        totalValueFormatter={(x) => x.toString()}
                        data={prepareChartData(currentGroupStats, "numberOfReservationsReceived")}
                        dataPreviousPeriod={prepareChartData(previousPeriodGroupStats, "numberOfReservationsReceived")}
                        dataPreviousYear={prepareChartData(previousYearGroupStats, "numberOfReservationsReceived")}
                        barValueFormatter={(x) => x.toString()}
                        period={periodNameToOverviewCardString(selectedYear, PeriodName[selectedPeriod as keyof typeof PeriodName])}
                        showPreviousYear={!isPreviousPeriodSameAsPreviousYear(selectedPeriod)}
                        totalPreviousPeriod={previousPeriodTotalStats.numberOfReservationsReceived}
                        totalPreviousYear={previousYearTotalStats.numberOfReservationsReceived}
                    />
                    <BarChartStatsCard
                        metric={"Average Length of Stay"}
                        total={totalStats.averageLengthOfStay}
                        totalValueFormatter={(x) => x.toFixed(1)}
                        data={prepareChartData(currentGroupStats, "averageLengthOfStay")}
                        dataPreviousPeriod={prepareChartData(previousPeriodGroupStats, "averageLengthOfStay")}
                        dataPreviousYear={prepareChartData(previousYearGroupStats, "averageLengthOfStay")}
                        barValueFormatter={(x) => x.toFixed(1)}
                        period={periodNameToOverviewCardString(selectedYear, PeriodName[selectedPeriod as keyof typeof PeriodName])}
                        showPreviousYear={!isPreviousPeriodSameAsPreviousYear(selectedPeriod)}
                        totalPreviousPeriod={previousPeriodTotalStats.averageLengthOfStay}
                        totalPreviousYear={previousYearTotalStats.averageLengthOfStay}
                    />
                    <BarChartStatsCard
                        metric={"Net Adr"}
                        total={totalStats.netAdr}
                        totalValueFormatter={overViewAdrFormatter}
                        data={prepareChartData(currentGroupStats, "netAdr")}
                        dataPreviousPeriod={prepareChartData(previousPeriodGroupStats, "netAdr")}
                        dataPreviousYear={prepareChartData(previousYearGroupStats, "netAdr")}
                        barValueFormatter={overViewBarChartAdrFormatter}
                        period={periodNameToOverviewCardString(selectedYear, PeriodName[selectedPeriod as keyof typeof PeriodName])}
                        showPreviousYear={!isPreviousPeriodSameAsPreviousYear(selectedPeriod)}
                        totalPreviousPeriod={previousPeriodTotalStats.netAdr}
                        totalPreviousYear={previousYearTotalStats.netAdr}
                    />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
                    <div data-testid="overview-average-gross-income-per-unit">
                        <BarChartStatsCard
                            metric="Average Gross Income Per Unit"
                            total={perUnit(totalStats.grossIncome, totalStats.numberOfApartments)}
                            totalValueFormatter={overViewIncomeFormatter}
                            data={prepareChartData(currentGroupStats, "grossIncome", true)}
                            dataPreviousPeriod={prepareChartData(previousPeriodGroupStats, "grossIncome", true)}
                            dataPreviousYear={prepareChartData(previousYearGroupStats, "grossIncome", true)}
                            barValueFormatter={overViewBarChartIncomeFormatter}
                            period={periodNameToOverviewCardString(selectedYear, PeriodName[selectedPeriod as keyof typeof PeriodName])}
                            showPreviousYear={!isPreviousPeriodSameAsPreviousYear(selectedPeriod)}
                            totalPreviousPeriod={perUnit(previousPeriodTotalStats.grossIncome, previousPeriodTotalStats.numberOfApartments)}
                            totalPreviousYear={perUnit(previousYearTotalStats.grossIncome, previousYearTotalStats.numberOfApartments)}
                        />
                    </div>
                    <div data-testid="overview-average-net-income-per-unit">
                        <BarChartStatsCard
                            metric="Average Net Income Per Unit"
                            total={perUnit(totalStats.netIncome, totalStats.numberOfApartments)}
                            totalValueFormatter={overViewIncomeFormatter}
                            data={prepareChartData(currentGroupStats, "netIncome", true)}
                            dataPreviousPeriod={prepareChartData(previousPeriodGroupStats, "netIncome", true)}
                            dataPreviousYear={prepareChartData(previousYearGroupStats, "netIncome", true)}
                            barValueFormatter={overViewBarChartIncomeFormatter}
                            period={periodNameToOverviewCardString(selectedYear, PeriodName[selectedPeriod as keyof typeof PeriodName])}
                            showPreviousYear={!isPreviousPeriodSameAsPreviousYear(selectedPeriod)}
                            totalPreviousPeriod={perUnit(previousPeriodTotalStats.netIncome, previousPeriodTotalStats.numberOfApartments)}
                            totalPreviousYear={perUnit(previousYearTotalStats.netIncome, previousYearTotalStats.numberOfApartments)}
                        />
                    </div>
                    <div data-testid="overview-average-payout-total">
                        <BarChartStatsCard
                            metric="Average Payout Per Unit"
                            total={averagePayout}
                            totalValueFormatter={overViewIncomeFormatter}
                            data={averageGroupPayout}
                            dataPreviousPeriod={averageGroupPayoutPrevious}
                            dataPreviousYear={averageGroupPayoutPreviousYear}
                            barValueFormatter={overViewBarChartIncomeFormatter}
                            period={periodNameToOverviewCardString(selectedYear, PeriodName[selectedPeriod as keyof typeof PeriodName])}
                            showPreviousYear={!isPreviousPeriodSameAsPreviousYear(selectedPeriod)}
                            totalPreviousPeriod={averagePayoutPrevious}
                            totalPreviousYear={averagePayoutPreviousYear}
                        />
                    </div>
                    <BarChartStatsCard
                        metric="Average BLT"
                        total={totalStats.averageBlt}
                        totalPreviousPeriod={previousPeriodTotalStats.averageBlt}
                        totalPreviousYear={previousYearTotalStats.averageBlt}
                        data={prepareChartData(currentGroupStats, "averageBlt")}
                        dataPreviousPeriod={prepareChartData(previousPeriodGroupStats, "averageBlt")}
                        dataPreviousYear={prepareChartData(previousYearGroupStats, "averageBlt")}
                        totalValueFormatter={(x) => x.toFixed(1)}
                        barValueFormatter={(x) => x.toFixed(1)}
                        period={periodNameToOverviewCardString(selectedYear, PeriodName[selectedPeriod as keyof typeof PeriodName])}
                        showPreviousYear={!isPreviousPeriodSameAsPreviousYear(selectedPeriod)}
                    />
                </div>
            </div>
        </div>
    );
};

export default OverviewPageNew;
